import React from "react";
import PropTypes from "prop-types";
import { Breadcrumbs, Typography, Box } from "@mui/material";
import { NavigateNext as NavigateNextIcon } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { colors } from "../../../../theme";

/**
 * CommonBreadcrumbs Component
 * Standardized Breadcrumb Navigation matching AutoBET Design System.
 *
 * @param {Object} props
 * @param {Array<Object|string|React.ReactNode>} [props.items] - Array of breadcrumb items
 * @param {React.ReactNode} [props.separator] - Custom separator component
 * @param {Object} [props.sx] - Custom sx styles for Breadcrumbs container
 * @param {string} [props.className] - CSS class name
 * @param {React.ReactNode} [props.children] - Children to render if items array is not provided
 */
const CommonBreadcrumbs = ({
  items,
  separator,
  sx = {},
  className,
  children,
  ...rest
}) => {
  const defaultSeparator = (
    <NavigateNextIcon
      fontSize="small"
      sx={{ color: colors.slate[400], fontSize: 18 }}
    />
  );

  return (
    <Breadcrumbs
      separator={separator || defaultSeparator}
      aria-label="breadcrumb"
      className={className}
      sx={{
        mb: 2.5,
        "& .MuiBreadcrumbs-separator": {
          mx: 0.75,
          display: "flex",
          alignItems: "center",
        },
        ...sx,
      }}
      {...rest}
    >
      {items && Array.isArray(items)
        ? items.map((item, index) => {
            const isLast = index === items.length - 1;

            if (React.isValidElement(item)) {
              return <React.Fragment key={index}>{item}</React.Fragment>;
            }

            if (typeof item === "string") {
              return (
                <Typography
                  key={index}
                  sx={{
                    color: isLast ? colors.slate[900] : colors.slate[500],
                    fontSize: "0.875rem",
                    fontWeight: isLast ? 600 : 500,
                  }}
                >
                  {item}
                </Typography>
              );
            }

            if (item && typeof item === "object") {
              const {
                label,
                path,
                to,
                href,
                icon,
                active,
                truncate,
                maxWidth = 320,
                color,
                sx: itemSx = {},
                style = {},
              } = item;

              const targetLink = path || to || href;
              const isItemActive = active !== undefined ? active : isLast;

              if (targetLink && !isItemActive) {
                return (
                  <Link
                    key={index}
                    to={targetLink}
                    style={{
                      color: colors.slate[500],
                      textDecoration: "none",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      transition: "color 0.15s ease",
                      ...style,
                    }}
                  >
                    {icon}
                    {label}
                  </Link>
                );
              }

              return (
                <Typography
                  key={index}
                  sx={{
                    color: color || (isItemActive ? colors.slate[900] : colors.slate[500]),
                    fontSize: "0.875rem",
                    fontWeight: isItemActive ? 600 : 500,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    ...(truncate
                      ? {
                          maxWidth,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }
                      : {}),
                    ...itemSx,
                  }}
                >
                  {icon}
                  {label}
                </Typography>
              );
            }

            return null;
          })
        : children}
    </Breadcrumbs>
  );
};

CommonBreadcrumbs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
      PropTypes.shape({
        label: PropTypes.node.isRequired,
        path: PropTypes.string,
        to: PropTypes.string,
        href: PropTypes.string,
        icon: PropTypes.node,
        active: PropTypes.bool,
        truncate: PropTypes.bool,
        maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        color: PropTypes.string,
        sx: PropTypes.object,
        style: PropTypes.object,
      }),
    ])
  ),
  separator: PropTypes.node,
  sx: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default CommonBreadcrumbs;
